/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import {
    ContextualMenuItemType,
    IContextualMenuItem,
    IContextualMenuItemStyleProps,
    IContextualMenuItemStyles,
    IContextualMenuProps,
    IContextualMenuStyleProps,
    IContextualMenuStyles
} from '@fluentui/react/lib/ContextualMenu';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logFilterClick, logSourceFilterClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { SourcesFiltersTypes, SourcesModalTypes } from 'src/qnaMaker/appShell/qnaMakerManageSourcesPage/QnaMakerManageSourcesPage';
import { QnaMakerSourceContentStructureKind, QnaMakerSourceType } from 'src/qnaMaker/core/services/sources/qnaMakerSourcesSwaggers';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';

export type QnaManageSourcesCommandBarProps = TableCommandBarProps<QnaMakerSourceStore> & {
    itemsLength: number;
    areActionsDisabled: boolean;
    isRefreshUrlDisabled: boolean;
    filteredKeys: SourcesFiltersTypes;
    isAddingChitchatDisabled: boolean;
    onRefreshURL: () => boolean | void;
    onDeleteSource: () => boolean | void;
    onRenameSource: () => boolean | void;
    onDownloadSource: () => boolean | void;
    onAddSources: (sourceType: SourcesModalTypes) => void;
    onChangeFilterKeys: (filterKey: SourcesFiltersTypes) => void;
};

const messages = defineMessages({
    addSource: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.addSource',
        defaultMessage: 'Add source'
    },
    addSourceURLs: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.addSourceURLs',
        defaultMessage: 'URLs'
    },
    addSourceFiles: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.addSourceFiles',
        defaultMessage: 'Files'
    },
    addSourceChitchat: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.addSourceChitchat',
        defaultMessage: 'Chitchat'
    },
    editSource: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.editSource',
        defaultMessage: 'Edit name'
    },
    downloadUnstructuredSource: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.downloadUnstructuredSource',
        defaultMessage: 'Download unstructured source'
    },
    deleteSource: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.deleteSource',
        defaultMessage: 'Delete'
    },
    search: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.search',
        defaultMessage: 'Search'
    },
    filter: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.filter',
        defaultMessage: 'Filter'
    },
    sourceType: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.sourceType',
        defaultMessage: 'Source type'
    },
    structureKind: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.structureKind',
        defaultMessage: 'Structure kind'
    },
    notSupportedLanguage: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.notSupportedLanguage',
        defaultMessage: 'There is no chitchat data for your resource language'
    },
    count: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.count',
        defaultMessage: 'items in list'
    },
    refreshUrl: {
        id: 'qnaMaker.qnaManageSourcesCommandBar.refreshUrl',
        defaultMessage: 'Refresh URL'
    }
});

const commandBarTheme = getScopedTheme('QnaManageSourcesCommandBar');
const menuStyles = classNamesFunction<IContextualMenuStyleProps, IContextualMenuStyles>()({
    header: { color: commandBarTheme.defaultTextColor },
    root: { margin: '10px 0' }
});
const menuButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ menuIcon: { display: 'none' } });
const headerStyles = classNamesFunction<IContextualMenuItemStyleProps, IContextualMenuItemStyles>()({ checkmarkIcon: { display: 'none' } });

export const QnaManageSourcesCommandBar = observer((props: QnaManageSourcesCommandBarProps) => {
    const {
        query = '',
        itemsLength,
        filteredKeys,
        onRefreshURL,
        onAddSources,
        onQueryChange,
        onRenameSource,
        onDeleteSource,
        selectedItems = [],
        areActionsDisabled,
        onChangeFilterKeys,
        isRefreshUrlDisabled,
        isAddingChitchatDisabled
    } = props;
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { uiStore, rootStore } = useGlobalState();
    const addSourceItemIdRef = React.useRef<string>(getId());

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const currentProjectId = rootStore.currentQnaMakerProjectStore?.id;

    React.useEffect(() => () => dequeueTeachingBubble('QnaAddSource'), []);

    React.useEffect(() => {
        if (canWrite && !areActionsDisabled && !uiStore.teachingBubbles.QnaAddSource) {
            createTeachingBubble(
                'QnaAddSource',
                localize(qnaMakerTeachingBubbleMessages.addSourceBody),
                localize(qnaMakerTeachingBubbleMessages.addSourceTitle),
                `#${addSourceItemIdRef.current}`,
                true,
                true
            );
        }
    }, [canWrite, areActionsDisabled, Object.values(uiStore.teachingBubbles)]);

    const renderFilterItem = (filterKey: QnaMakerSourceType | QnaMakerSourceContentStructureKind, isSourceKindFilter: boolean) => (
        <Checkbox
            key={filterKey}
            styles={{
                root: { minHeight: '36px', width: '100%', paddingLeft: '8px' }
            }}
            checked={
                isSourceKindFilter
                    ? filteredKeys.sourceKind.some(f => f === filterKey)
                    : filteredKeys.contentStructureKind.some(f => f === filterKey)
            }
            onChange={() => {
                const checked = isSourceKindFilter
                    ? filteredKeys.sourceKind.some(f => f === filterKey)
                    : filteredKeys.contentStructureKind.some(f => f === filterKey);

                if (!checked) {
                    logFilterClick(serviceName, 'Sources', 'FilterSelection', null, currentProjectId);

                    if (isSourceKindFilter) {
                        logSourceFilterClick(serviceName, 'Sources', null, currentProjectId);
                    }
                }

                const filteredItems = { ...filteredKeys };
                if (isSourceKindFilter) {
                    filteredItems.sourceKind = filteredItems.sourceKind.some(p => p === filterKey)
                        ? filteredKeys.sourceKind.filter(c => c !== filterKey)
                        : ([...filteredItems.sourceKind, filterKey] as QnaMakerSourceType[]);
                } else {
                    filteredItems.contentStructureKind = filteredItems.contentStructureKind.some(p => p === filterKey)
                        ? filteredKeys.contentStructureKind.filter(c => c !== filterKey)
                        : ([...filteredItems.contentStructureKind, filterKey] as QnaMakerSourceContentStructureKind[]);
                }
                onChangeFilterKeys(filteredItems);
            }}
            onRenderLabel={() => <TruncatedText title={filterKey}>{filterKey}</TruncatedText>}
            ariaLabel={filterKey}
        />
    );

    const addSourcesSubMenuItems: IContextualMenuProps = {
        items: [
            { key: 'url', name: localize(messages.addSourceURLs), onClick: () => onAddSources('url') },
            { key: 'file', name: localize(messages.addSourceFiles), onClick: () => onAddSources('file') },
            {
                key: 'chitchat',
                name: localize(messages.addSourceChitchat),
                onClick: () => onAddSources('chitchat'),
                disabled: isAddingChitchatDisabled,
                title: isAddingChitchatDisabled ? localize(messages.notSupportedLanguage) : undefined
            }
        ]
    };

    const commands: ICommandBarItemProps[] = [
        {
            key: 'create',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={canWrite || areActionsDisabled || item.disabled}
                    data-automation-id="addSourceButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            data-automation-id="addSource"
                            id={addSourceItemIdRef.current}
                            menuProps={addSourcesSubMenuItems}
                            text={localize(messages.addSource)}
                            disabled={!canWrite || areActionsDisabled || item.disabled}
                            iconProps={{ iconName: 'Add', styles: { root: { color: 'rgb(0, 120, 212)' } } }}
                            styles={{
                                label: { fontWeight: 'normal' },
                                root: { height: '100%', border: 'none' },
                                ...(!canWrite ? getCommandBarButtonStyles(item.renderedInOverflow) : {})
                            }}
                        />
                    }
                />
            )
        },
        {
            key: 'rename',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    data-automation-id="renameSourceButtonLanguageToolTip"
                    hidden={canWrite || item.disabled || areActionsDisabled}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onRenameSource}
                            iconProps={{ iconName: 'Rename' }}
                            data-automation-id="renameSourceButton"
                            text={localize(messages.editSource)}
                            name={localize(messages.editSource)}
                            disabled={!canWrite || item.disabled || areActionsDisabled}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'refresh',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    data-automation-id="refreshUrlButtonLanguageToolTip"
                    hidden={canWrite || item.disabled || isRefreshUrlDisabled || areActionsDisabled}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onRefreshURL}
                            iconProps={{ iconName: 'Refresh' }}
                            ariaLabel={localize(messages.refreshUrl)}
                            data-automation-id="refreshUrlButton"
                            text={localize(messages.refreshUrl)}
                            name={localize(messages.refreshUrl)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canWrite || item.disabled || isRefreshUrlDisabled || areActionsDisabled}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteSourceButtonLanguageToolTip"
                    hidden={canWrite || item.disabled || areActionsDisabled}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onDeleteSource}
                            iconProps={{ iconName: 'Delete' }}
                            data-automation-id="deleteSourceButton"
                            text={localize(messages.deleteSource)}
                            name={localize(messages.deleteSource)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canWrite || item.disabled || areActionsDisabled}
                        />
                    }
                />
            )
        }
    ];

    const items: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['create'],
        ['rename', 'download', 'refresh', 'delete'],
        ['download', 'delete'],
        selectedItems.length
    );

    const filtersItems: IContextualMenuItem[] = [
        {
            key: 'unstructured',
            text: 'unstructured',
            onRender: () => renderFilterItem('unstructured', false)
        },
        {
            key: 'sourceTypeHeader',
            itemProps: { styles: headerStyles },
            text: localize(messages.sourceType),
            itemType: ContextualMenuItemType.Header
        },
        { key: 'sourceTypeHeaderDivider', itemType: ContextualMenuItemType.Divider },
        {
            key: 'url',
            text: 'url',
            onRender: () => renderFilterItem('url', true)
        },
        {
            key: 'file',
            text: 'file',
            onRender: () => renderFilterItem('file', true)
        }
    ];

    const farItems: ICommandBarItemProps[] = [
        {
            key: 'projectsCount',
            onRender: () => (
                <LuisText styles={{ root: { display: 'flex', alignItems: 'center', marginRight: '5px' } }}>
                    {itemsLength} {localize(messages.count)}
                </LuisText>
            )
        },
        {
            key: 'searchBox',
            onRender: () => (
                <LuisDebouncedCollapsableSearchBox
                    placeholder={localize(messages.search)}
                    size={36}
                    value={query}
                    disabled={!query && !itemsLength}
                    onChange={(_, newValue?: string) => onQueryChange(newValue)}
                />
            )
        },
        {
            key: 'filter',
            buttonStyles: menuButtonStyles,
            text: localize(messages.filter),
            iconProps: { iconName: 'Filter' },
            subMenuProps: { items: filtersItems, styles: menuStyles }
        }
    ];

    return <LuisCommandBar items={items} farItems={farItems} />;
});
